import { useState, useRef, useEffect, useCallback } from 'react';

interface ScrollableNumbersProps {
  min?: number;
  max?: number;
  value: number;
  onChange: (value: number) => void;
  itemHeight?: number;
  visibleCount?: number;
}

function itemStyle(distance: number): { fontSize: number; opacity: number } {
  if (distance < 0.6) return { fontSize: 23, opacity: 1 };
  if (distance < 1.5) return { fontSize: 19, opacity: 0.35 };
  if (distance < 2.5) return { fontSize: 15, opacity: 0.2 };
  return { fontSize: 11, opacity: 0.1 };
}

export function ScrollableNumbers({
  min = 1,
  max = 20,
  value,
  onChange,
  itemHeight = 31,
  visibleCount = 7,
}: ScrollableNumbersProps) {
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  const pad = Math.floor(visibleCount / 2);
  const containerH = itemHeight * visibleCount;

  const [scrollTop, setScrollTop] = useState(() => (value - min) * itemHeight);
  const listRef = useRef<HTMLDivElement>(null);
  const snapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the last value notified via onChange to prevent the sync effect
  // from re-firing when the parent echoes the same value back as a prop update.
  // NaN ensures the sync effect always fires on mount to set the initial scroll position.
  const lastNotified = useRef(NaN);

  useEffect(() => {
    if (lastNotified.current === value) return;
    lastNotified.current = value;
    const target = (value - min) * itemHeight;
    setScrollTop(target);
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = target;
    });
  }, [value, min, itemHeight]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    setScrollTop(listRef.current.scrollTop);
    if (snapTimer.current) clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      if (!listRef.current) return;
      const idx = Math.round(listRef.current.scrollTop / itemHeight);
      const clamped = Math.max(0, Math.min(idx, numbers.length - 1));
      listRef.current.scrollTop = clamped * itemHeight;
      setScrollTop(clamped * itemHeight);
      const next = numbers[clamped];
      lastNotified.current = next;
      onChange(next);
    }, 80);
  }, [numbers, itemHeight, onChange]);

  const centerIndex = scrollTop / itemHeight;

  return (
    <div className="relative w-full" style={{ height: containerH }}>
      <div
        className="pointer-events-none absolute inset-x-6.25 z-0 rounded-lg bg-[#eaeaea]"
        style={{ top: itemHeight * pad, height: 34 }}
      />
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="relative z-10 h-full scrollbar-none overflow-y-scroll"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        <div style={{ height: itemHeight * pad }} />
        {numbers.map((n, i) => {
          const distance = Math.abs(i - centerIndex);
          const s = itemStyle(distance);
          return (
            <div
              key={n}
              style={{ height: itemHeight, scrollSnapAlign: 'center' }}
              className="flex items-center justify-center"
            >
              <span
                className="font-pp-neue-montreal font-normal select-none"
                style={{
                  fontSize: s.fontSize,
                  color: '#333',
                  opacity: s.opacity,
                  transition: 'font-size 80ms ease-out, opacity 80ms ease-out',
                }}
              >
                {n}
              </span>
            </div>
          );
        })}
        <div style={{ height: itemHeight * pad }} />
      </div>
    </div>
  );
}
